import { useMemo, useState, useRef } from "react";
import { useRoute, useLocation } from "wouter";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Save, Send, Check, X, Paperclip, FileCheck, Activity,
  ChevronDown, Loader2, Building2,
} from "lucide-react";
import { QA, EVIDENCE_ACCEPT, fileToBase64, useOpenEvidence, QaCrumb } from "./qa-shared";

type Avail = "yes" | "no" | null;
type Practice = "yes" | "no" | "na" | null;

interface RState {
  availability: Avail;
  beingInPractice: Practice;
  availabilityReason: string;
  beingInPracticeReason: string;
  specificRemarks: string;
}

export default function QaQuestionnaire() {
  const [, params] = useRoute("/qa/visits/:id/questionnaire");
  const [, setLocation] = useLocation();
  const visitId = params?.id ? Number(params.id) : undefined;
  const openEvidence = useOpenEvidence();

  const { data, isLoading, refetch } = trpc.qa.questionnaire.get.useQuery(
    { visitId: visitId! },
    { enabled: !!visitId },
  );

  const saveResponse = trpc.qa.questionnaire.saveResponse.useMutation();
  const uploadEvidence = trpc.qa.questionnaire.uploadEvidence.useMutation();
  const deleteEvidence = trpc.qa.questionnaire.deleteEvidence.useMutation();
  const submit = trpc.qa.questionnaire.submit.useMutation();

  const [activeDomain, setActiveDomain] = useState(0);
  const [openCards, setOpenCards] = useState<Record<number, boolean>>({});
  const [state, setState] = useState<Record<number, RState>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [unansweredNotice, setUnansweredNotice] = useState<{ count: number; pendingDomainIndex: number } | null>(null);
  const [showOnlyUnanswered, setShowOnlyUnanswered] = useState(false);
  const initialized = useRef(false);

  // Seed local state from server responses once loaded.
  if (data && !initialized.current) {
    initialized.current = true;
    const seed: Record<number, RState> = {};
    const openAll: Record<number, boolean> = {};
    for (const q of data.questions) {
      const r = data.responses.find((x: any) => x.questionId === q.id);
      seed[q.id] = {
        availability: (r?.availability as Avail) ?? null,
        beingInPractice: (r?.beingInPractice as Practice) ?? null,
        availabilityReason: r?.availabilityReason ?? "",
        beingInPracticeReason: r?.beingInPracticeReason ?? "",
        specificRemarks: r?.specificRemarks ?? "",
      };
      openAll[q.id] = true; // cards show their answer controls inline by default
    }
    setState(seed);
    setOpenCards(openAll);
  }

  const domains = data?.domains ?? [];
  const sections = data?.sections ?? [];
  const questions = data?.questions ?? [];
  const locked = data?.visit && data.visit.status !== "scheduled";

  const attachmentsByQ = useMemo(() => {
    const map: Record<number, any[]> = {};
    for (const a of data?.attachments ?? []) {
      (map[a.questionId] ??= []).push(a);
    }
    return map;
  }, [data?.attachments]);

  const stats = useMemo(() => {
    let answered = 0, yes = 0, no = 0;
    const perDomain: Record<number, { answered: number; total: number }> = {};
    for (const d of domains) perDomain[d.id] = { answered: 0, total: 0 };
    for (const q of questions) {
      const st = state[q.id];
      perDomain[q.domainId] ??= { answered: 0, total: 0 };
      perDomain[q.domainId].total++;
      if (st?.availability) {
        answered++;
        perDomain[q.domainId].answered++;
        if (st.availability === "yes") yes++;
        if (st.availability === "no") no++;
      }
    }
    const total = questions.length;
    const compliance = yes + no > 0 ? Math.round((yes / (yes + no)) * 100) : null;
    return { answered, yes, no, total, compliance, perDomain };
  }, [state, questions, domains]);

  function persist(questionId: number, patch: Partial<RState>) {
    const next = { ...(state[questionId] ?? {
      availability: null, beingInPractice: null, availabilityReason: "", beingInPracticeReason: "", specificRemarks: "",
    }), ...patch };
    setState((s) => ({ ...s, [questionId]: next }));
    if (!visitId) return;
    saveResponse.mutate({
      visitId,
      questionId,
      availability: next.availability,
      availabilityReason: next.availabilityReason || null,
      beingInPractice: next.beingInPractice,
      beingInPracticeReason: next.beingInPracticeReason || null,
      specificRemarks: next.specificRemarks || null,
    });
  }

  async function handleUpload(questionId: number, file: File) {
    if (!visitId) return;
    try {
      const base64Data = await fileToBase64(file);
      await uploadEvidence.mutateAsync({ visitId, questionId, fileName: file.name, mimeType: file.type, base64Data });
      toast.success("Evidence uploaded");
      refetch();
    } catch (e: any) {
      toast.error(e?.message ?? "Upload failed");
    }
  }

  async function doSubmit() {
    if (!visitId) return;
    try {
      await submit.mutateAsync({ visitId });
      setConfirmOpen(false);
      toast.success("Questionnaire submitted to the QAG team.");
      setLocation(`/qa/visits/${visitId}`);
    } catch (e: any) {
      toast.error(e?.message ?? "Submit failed");
    }
  }

  if (isLoading) {
    return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" style={{ color: QA.purple }} /></div>;
  }
  if (!data) return <div className="p-6 text-sm text-gray-500">Visit not found.</div>;

  const domain = domains[activeDomain];
  const domainSections = sections.filter((s: any) => s.domainId === domain?.id);

  return (
    <div style={{ background: QA.surface }} className="min-h-screen -mx-4 -my-4 md:-mx-6 md:-my-6">
      {/* Page header */}
      <div className="border-b px-6 py-5" style={{ background: QA.surface, borderColor: QA.border }}>
        <QaCrumb backTo={`/qa/visits/${visitId}`} items={[{ label: "Visits", to: "/qa/visits" }, { label: data.visit?.visitNumber ?? `Visit #${visitId}`, to: `/qa/visits/${visitId}`, mono: true }, { label: "Questionnaire" }]} />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-[20px] font-bold tracking-tight" style={{ color: QA.text }}>QA Visit Questionnaire</h1>
            <p className="mt-0.5 text-[13px]" style={{ color: QA.text2 }}>
              Pre-Visit Questionnaire — Mandatory &amp; Recommended Documents / Being in Practice Assessment
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px]" style={{ background: QA.grayPale, color: QA.text2, border: `1px solid ${QA.border}` }}>
              <Building2 className="h-3.5 w-3.5" /> {data.visit.siteName ?? `Site #${data.visit.siteId}`}
            </div>
          </div>
        </div>
      </div>

      {/* Progress strip */}
      <div className="flex items-center gap-4 border-b px-6 py-3" style={{ background: QA.surface, borderColor: QA.border }}>
        <span className="whitespace-nowrap text-[12px]" style={{ color: QA.text2 }}>{stats.answered} of {stats.total} questions answered</span>
        <div className="h-1.5 flex-1 overflow-hidden rounded-full" style={{ background: QA.border }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${stats.total ? (stats.answered / stats.total) * 100 : 0}%`, background: `linear-gradient(90deg, ${QA.purple}, ${QA.purpleSoft})` }} />
        </div>
        <span className="min-w-[36px] text-right text-[12px] font-bold" style={{ color: QA.purple }}>{stats.total ? Math.round((stats.answered / stats.total) * 100) : 0}%</span>
      </div>

      <div className="flex">
        {/* Sidebar: domain nav + score */}
        <aside className="hidden w-[260px] shrink-0 border-r p-4 md:block" style={{ background: QA.surface, borderColor: QA.border }}>
          <div className="rounded-xl border p-3" style={{ background: QA.purpleXPale, borderColor: QA.border }}>
            <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: QA.text3 }}>Overall Compliance</div>
            <div className="text-[26px] font-bold leading-tight" style={{ color: stats.compliance == null ? QA.purple : stats.compliance >= 80 ? QA.green : stats.compliance >= 70 ? QA.amber : QA.red }}>
              {stats.compliance == null ? "—" : `${stats.compliance}%`}
            </div>
            <div className="grid grid-cols-2 gap-1.5 pt-2">
              <MiniStat value={stats.yes} label="Available" color={QA.green} />
              <MiniStat value={stats.no} label="Not Available" color={QA.red} />
              <MiniStat value={stats.answered} label="Answered" color={QA.purple} />
              <MiniStat value={stats.total - stats.answered} label="Remaining" color={QA.amber} />
            </div>
          </div>

          <div className="mt-3 space-y-0.5">
            {domains.map((d: any, di: number) => {
              const pd = stats.perDomain[d.id] ?? { answered: 0, total: 0 };
              const pct = pd.total ? Math.round((pd.answered / pd.total) * 100) : 0;
              const active = di === activeDomain;
              const done = pd.total > 0 && pd.answered === pd.total;
              return (
                <button key={d.id} onClick={() => setActiveDomain(di)} className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors"
                  style={{ background: active ? QA.purpleXPale : "transparent", borderLeft: `3px solid ${active ? QA.purple : "transparent"}` }}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12px] font-semibold" style={{ color: active ? QA.purple : QA.text }}>{d.name}</div>
                    <div className="text-[10px]" style={{ color: QA.text3 }}>{pd.total} questions · {(d.weightBps / 100).toFixed(0)}%</div>
                  </div>
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: done ? QA.green : active ? QA.purple : QA.grayPale, color: done || active ? "#fff" : QA.text2 }}>{pct}%</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Main: domain panel */}
        <main className="min-w-0 flex-1 px-6 pb-28 pt-5">
          {domain && (
            <>
              <div className="mb-5 flex items-center gap-3.5 rounded-2xl border p-5 shadow-sm" style={{ background: QA.surface, borderColor: QA.border, borderLeft: `5px solid ${QA.purple}` }}>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white" style={{ background: QA.purple }}>
                  <FileCheck className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[16px] font-bold" style={{ color: QA.text }}>{domain.name}</div>
                  {domain.description && <div className="mt-0.5 text-[12px]" style={{ color: QA.text2 }}>{domain.description}</div>}
                  <div className="mt-1.5 flex gap-1.5">
                    <span className="rounded-full px-2.5 py-0.5 text-[10px] font-bold" style={{ background: QA.purplePale, color: QA.purple }}>{(domain.weightBps / 100).toFixed(0)}% domain weight</span>
                  </div>
                </div>
              </div>

              {showOnlyUnanswered && (
                <div className="mb-5 flex items-center gap-2 rounded-lg border-l-4 p-3" style={{ background: QA.amberPale, borderColor: QA.amber }}>
                  <i className="ti ti-filter-x" style={{ color: QA.amber, fontSize: 16 }} />
                  <div className="flex-1">
                    <div className="text-[12px] font-semibold" style={{ color: QA.amber }}>Showing unanswered questions only — Answer them and clear filter when done</div>
                  </div>
                  <button onClick={() => setShowOnlyUnanswered(false)} className="text-[11px] font-semibold px-3 py-1 rounded-md" style={{ color: QA.amber, borderColor: QA.amber, border: `1px solid ${QA.amber}` }}>
                    Clear Filter
                  </button>
                </div>
              )}

              {domainSections.map((section: any) => {
                let sectionQuestions = questions.filter((q: any) => q.sectionId === section.id);
                if (showOnlyUnanswered) {
                  sectionQuestions = sectionQuestions.filter((q: any) => !state[q.id]?.availability);
                }
                return sectionQuestions.length > 0 ? (
                  <div key={section.id} className="mb-5">
                    <div className="mb-2.5 flex items-center gap-2 border-b pb-2.5 text-[11px] font-bold uppercase tracking-wide" style={{ color: QA.text3, borderColor: QA.border }}>
                      <span className="inline-block h-3.5 w-[3px] rounded" style={{ background: QA.purple }} /> {section.name}
                    </div>
                    {sectionQuestions.map((q: any) => (
                      <QuestionCard
                        key={q.id}
                        q={q}
                        st={state[q.id]}
                        open={!!openCards[q.id]}
                        locked={!!locked}
                        attachments={attachmentsByQ[q.id] ?? []}
                        onToggle={() => setOpenCards((o) => ({ ...o, [q.id]: !o[q.id] }))}
                        onChange={(patch) => persist(q.id, patch)}
                        onUpload={(file) => handleUpload(q.id, file)}
                        onDeleteAttachment={async (id) => { await deleteEvidence.mutateAsync({ id }); refetch(); }}
                        onOpenEvidence={openEvidence}
                      />
                    ))}
                  </div>
                ) : null;
              })}
            </>
          )}
        </main>
      </div>

      {/* Bottom bar */}
      <div className="fixed bottom-0 right-0 z-40 flex items-center justify-between gap-3 border-t px-6 py-3 md:left-[var(--sidebar-w,0px)]" style={{ left: 0, background: QA.surface, borderColor: QA.border, boxShadow: "0 -4px 20px rgba(79,0,140,0.08)" }}>
        <button onClick={() => setLocation(`/qa/visits/${visitId}`)}
          className="inline-flex items-center gap-1.5 rounded-lg border px-4 py-2 text-[12px] font-semibold" style={{ borderColor: QA.border, color: QA.text }}>
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Visit
        </button>
        <div className="text-center text-[12px]" style={{ color: QA.text2 }}>
          <div className="font-semibold" style={{ color: QA.text }}>{domain?.name}</div>
          <div className="text-[11px]" style={{ color: QA.text3 }}>Domain {activeDomain + 1} of {domains.length}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setActiveDomain((d) => Math.max(0, d - 1))} disabled={activeDomain === 0}
            className="inline-flex items-center gap-1.5 rounded-lg border px-4 py-2 text-[12px] font-semibold disabled:opacity-40" style={{ borderColor: QA.border, color: QA.text }}>
            <ArrowLeft className="h-3.5 w-3.5" /> Previous
          </button>
          <button onClick={() => {
            if (activeDomain >= domains.length - 1) return;
            const currentDomainId = domain?.id;
            const currentStats = stats.perDomain[currentDomainId];
            const unansweredCount = (currentStats?.total ?? 0) - (currentStats?.answered ?? 0);
            if (unansweredCount > 0) {
              setUnansweredNotice({ count: unansweredCount, pendingDomainIndex: activeDomain + 1 });
            } else {
              setActiveDomain((d) => Math.min(domains.length - 1, d + 1));
            }
          }} disabled={activeDomain >= domains.length - 1}
            className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold text-white disabled:opacity-40" style={{ background: QA.purple }}>
            Next Domain <ArrowRight className="h-3.5 w-3.5" />
          </button>
          {locked ? (
            <span className="rounded-lg px-4 py-2 text-[12px] font-semibold" style={{ background: QA.greenPale, color: QA.green }}>Submitted</span>
          ) : (
            <button onClick={() => setConfirmOpen(true)} disabled={submit.isPending}
              className="inline-flex min-w-[150px] items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold text-white" style={{ background: QA.purple }}>
              {submit.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Submit Questionnaire
            </button>
          )}
        </div>
      </div>

      {confirmOpen && (
        <SubmitModal
          total={stats.total}
          answered={stats.answered}
          pending={submit.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={doSubmit}
        />
      )}

      {unansweredNotice && (
        <UnansweredNotice
          count={unansweredNotice.count}
          onStay={() => {
            setUnansweredNotice(null);
            setShowOnlyUnanswered(true);
          }}
          onContinue={() => {
            setActiveDomain(unansweredNotice.pendingDomainIndex);
            setUnansweredNotice(null);
          }}
        />
      )}
    </div>
  );
}

function SubmitModal({ total, answered, pending, onCancel, onConfirm }: { total: number; answered: number; pending: boolean; onCancel: () => void; onConfirm: () => void }) {
  const remaining = total - answered;
  const complete = remaining === 0;
  return (
    <div onClick={onCancel} className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(26,16,37,0.45)" }}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[440px] overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center gap-3 px-6 pt-6">
          <div className="flex h-11 w-11 items-center justify-center rounded-full" style={{ background: complete ? QA.greenPale : QA.amberPale, color: complete ? QA.green : QA.amber }}>
            <i className={`ti ${complete ? "ti-circle-check" : "ti-alert-triangle"}`} style={{ fontSize: 22 }} />
          </div>
          <div>
            <div className="text-[16px] font-bold" style={{ color: QA.text }}>Submit questionnaire?</div>
            <div className="text-[12px]" style={{ color: QA.text3 }}>This sends it to the QAG team for review.</div>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="rounded-xl border p-4" style={{ borderColor: QA.border, background: QA.bg }}>
            <div className="flex items-center justify-between text-[13px]">
              <span style={{ color: QA.text2 }}>Answered</span>
              <span className="font-bold" style={{ color: QA.text }}>{answered} / {total}</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full" style={{ background: QA.grayPale }}>
              <div className="h-full rounded-full" style={{ width: `${total ? (answered / total) * 100 : 0}%`, background: complete ? QA.green : QA.purple }} />
            </div>
            {complete ? (
              <div className="mt-3 flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: QA.green }}>
                <i className="ti ti-circle-check" /> All {total} questions answered.
              </div>
            ) : (
              <div className="mt-3 text-[12.5px]" style={{ color: QA.amber }}>
                <b>{remaining}</b> of {total} question{remaining === 1 ? "" : "s"} {remaining === 1 ? "is" : "are"} still unanswered. You can submit anyway — unanswered items will be flagged as incomplete to the QAG team.
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2.5 border-t px-6 py-4" style={{ borderColor: QA.border }}>
          <button onClick={onCancel} className="rounded-lg border px-4 py-2 text-[13px] font-semibold" style={{ borderColor: QA.border, color: QA.text2 }}>Keep editing</button>
          <button onClick={onConfirm} disabled={pending} className="inline-flex items-center gap-1.5 rounded-lg px-5 py-2 text-[13px] font-semibold text-white disabled:opacity-60" style={{ background: complete ? QA.green : QA.purple }}>
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {complete ? "Submit" : "Submit anyway"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div className="rounded-md border bg-white p-2 text-center" style={{ borderColor: QA.border }}>
      <div className="text-[16px] font-bold leading-none" style={{ color }}>{value}</div>
      <div className="mt-0.5 text-[10px]" style={{ color: QA.text3 }}>{label}</div>
    </div>
  );
}

function ToggleBtn({ active, kind, onClick, children, disabled }: { active: boolean; kind: "yes" | "no" | "na"; onClick: () => void; children: React.ReactNode; disabled?: boolean }) {
  const palette = kind === "yes" ? { c: QA.green, bg: QA.greenPale } : kind === "no" ? { c: QA.red, bg: QA.redPale } : { c: QA.purple, bg: QA.purpleXPale };
  return (
    <button type="button" disabled={disabled} onClick={onClick}
      className="flex flex-1 items-center justify-center gap-1.5 rounded-md border-[1.5px] px-2.5 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-60"
      style={active ? { background: palette.bg, color: palette.c, borderColor: palette.c } : { background: "#fff", color: QA.text2, borderColor: QA.border }}>
      {children}
    </button>
  );
}

function QuestionCard({ q, st, open, locked, attachments, onToggle, onChange, onUpload, onDeleteAttachment, onOpenEvidence }: {
  q: any; st?: RState; open: boolean; locked: boolean; attachments: any[];
  onToggle: () => void; onChange: (p: Partial<RState>) => void; onUpload: (f: File) => void;
  onDeleteAttachment: (id: number) => void; onOpenEvidence: (k: string) => void;
}) {
  const s = st ?? { availability: null, beingInPractice: null, availabilityReason: "", beingInPracticeReason: "", specificRemarks: "" };
  const fileRef = useRef<HTMLInputElement>(null);
  const borderLeft = s.availability === "yes" ? `4px solid ${QA.green}` : s.availability === "no" ? `4px solid ${QA.red}` : undefined;

  return (
    <div className="mb-2 overflow-hidden rounded-lg border" style={{ background: QA.surface, borderColor: QA.border, borderLeft }}>
      <div className="flex cursor-pointer items-start gap-3 p-3.5" onClick={onToggle}>
        <span className="mt-0.5 shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-bold" style={{ background: QA.purpleXPale, color: QA.purple }}>{q.questionNumber}</span>
        <span className="flex-1 text-[13px] leading-relaxed" style={{ color: QA.text }}>{q.text}</span>
        <div className="mt-0.5 flex shrink-0 items-center gap-1.5">
          <StatusDot active={s.availability} icon={<FileCheck className="h-3 w-3" />} />
          <PracticeDot value={s.beingInPractice} />
          <ChevronDown className="h-4 w-4 transition-transform" style={{ color: QA.text3, transform: open ? "rotate(180deg)" : undefined }} />
        </div>
      </div>

      {open && (
        <div className="border-t px-4 pb-3.5" style={{ borderColor: QA.border, background: QA.surface }}>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {/* Availability */}
            <div className="flex flex-col gap-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: QA.text2 }}>Availability <span style={{ color: QA.red }}>*</span></div>
              <div className="flex gap-1.5">
                <ToggleBtn active={s.availability === "yes"} kind="yes" disabled={locked} onClick={() => onChange({ availability: s.availability === "yes" ? null : "yes" })}><Check className="h-3.5 w-3.5" /> Yes</ToggleBtn>
                <ToggleBtn active={s.availability === "no"} kind="no" disabled={locked} onClick={() => onChange({ availability: s.availability === "no" ? null : "no" })}><X className="h-3.5 w-3.5" /> No</ToggleBtn>
              </div>
              {s.availability === "no" && (
                <ReasonInput value={s.availabilityReason} disabled={locked} placeholder="State why this document / evidence is not available..." onChange={(v) => onChange({ availabilityReason: v })} />
              )}
            </div>
            {/* Being in practice */}
            <div className="flex flex-col gap-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: QA.text2 }}>Being in Practice <span style={{ color: QA.red }}>*</span></div>
              <div className="flex gap-1.5">
                <ToggleBtn active={s.beingInPractice === "yes"} kind="yes" disabled={locked} onClick={() => onChange({ beingInPractice: s.beingInPractice === "yes" ? null : "yes" })}><Check className="h-3.5 w-3.5" /> Yes</ToggleBtn>
                <ToggleBtn active={s.beingInPractice === "no"} kind="no" disabled={locked} onClick={() => onChange({ beingInPractice: s.beingInPractice === "no" ? null : "no" })}><X className="h-3.5 w-3.5" /> No</ToggleBtn>
                <ToggleBtn active={s.beingInPractice === "na"} kind="na" disabled={locked} onClick={() => onChange({ beingInPractice: s.beingInPractice === "na" ? null : "na" })}>N/A</ToggleBtn>
              </div>
              {s.beingInPractice === "no" && (
                <ReasonInput value={s.beingInPracticeReason} disabled={locked} placeholder="State why this is not being practiced..." onChange={(v) => onChange({ beingInPracticeReason: v })} />
              )}
            </div>
          </div>

          {/* Remarks + evidence */}
          <div className="mt-3">
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: QA.text2 }}>Specific Remarks</div>
            <div className="grid grid-cols-[1fr_auto] items-start gap-2.5">
              <textarea rows={2} defaultValue={s.specificRemarks} disabled={locked} placeholder="Add specific remarks, notes or observations..."
                onBlur={(e) => e.target.value !== s.specificRemarks && onChange({ specificRemarks: e.target.value })}
                className="w-full resize-none rounded-md border-[1.5px] px-2.5 py-2 text-[12px] outline-none" style={{ borderColor: QA.border, background: "#fff", color: QA.text }} />
              <div className="flex flex-col items-end gap-1">
                <input ref={fileRef} type="file" accept={EVIDENCE_ACCEPT} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
                <button type="button" disabled={locked} onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border-[1.5px] border-dashed px-3.5 py-2 text-[11px] font-semibold disabled:opacity-50" style={{ borderColor: QA.borderMid, color: QA.text2 }}>
                  <Paperclip className="h-3.5 w-3.5" /> Attach Evidence
                  {attachments.length > 0 && <span className="rounded-full px-1.5 py-0.5 text-[10px] text-white" style={{ background: QA.purple }}>{attachments.length}</span>}
                </button>
                <div className="text-right text-[10px]" style={{ color: QA.text3 }}>PDF, DOCX, XLSX<br />JPG, PNG</div>
              </div>
            </div>
            {attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {attachments.map((a) => (
                  <span key={a.id} className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px]" style={{ borderColor: QA.border, background: "#fff", color: QA.text2 }}>
                    <button type="button" onClick={() => onOpenEvidence(a.fileKey)} className="max-w-[160px] truncate underline-offset-2 hover:underline">{a.fileName}</button>
                    {!locked && <button type="button" onClick={() => onDeleteAttachment(a.id)}><X className="h-3 w-3" style={{ color: QA.red }} /></button>}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusDot({ active, icon }: { active: Avail; icon: React.ReactNode }) {
  const style = active === "yes" ? { background: QA.greenPale, color: QA.green } : active === "no" ? { background: QA.redPale, color: QA.red } : { background: QA.grayPale, color: QA.text3 };
  return <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full" style={style} title="Availability"><FileCheck className="h-3 w-3" /></span>;
}

function PracticeDot({ value }: { value: Practice }) {
  const style = value === "yes" ? { background: QA.greenPale, color: QA.green } : value === "no" ? { background: QA.redPale, color: QA.red } : value === "na" ? { background: QA.purpleXPale, color: QA.purple } : { background: QA.grayPale, color: QA.text3 };
  return <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full" style={style} title="Being in Practice"><Activity className="h-3 w-3" /></span>;
}

function ReasonInput({ value, onChange, placeholder, disabled }: { value: string; onChange: (v: string) => void; placeholder: string; disabled?: boolean }) {
  return (
    <div className="mt-1.5 flex flex-col gap-1">
      <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: QA.red }}>Reason — If No, why?</div>
      <input defaultValue={value} disabled={disabled} placeholder={placeholder} onBlur={(e) => e.target.value !== value && onChange(e.target.value)}
        className="rounded-md border-[1.5px] px-2.5 py-2 text-[12px] outline-none" style={{ borderColor: "#FCA5A5", background: "#fff", color: QA.text }} />
    </div>
  );
}

function UnansweredNotice({ count, onStay, onContinue }: { count: number; onStay: () => void; onContinue: () => void }) {
  return (
    <div className="fixed right-6 bottom-24 z-50 max-w-sm animate-in slide-in-from-right-5 duration-300">
      <div className="rounded-lg border shadow-lg p-4" style={{ background: QA.surface, borderColor: QA.borderMid, boxShadow: "0 4px 20px rgba(79,0,140,0.15)" }}>
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ background: QA.amberPale, color: QA.amber }}>
            <i className="ti ti-alert-circle" style={{ fontSize: 18 }} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-[13px]" style={{ color: QA.text }}>Unanswered Questions</div>
            <div className="text-[12px] mt-1" style={{ color: QA.text2 }}>
              {count} question{count === 1 ? "" : "s"} on this domain {count === 1 ? "is" : "are"} still unanswered.
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={onStay} className="flex-1 rounded-md border px-3 py-1.5 text-[11px] font-semibold" style={{ borderColor: QA.border, color: QA.text }}>
                Stay & Answer
              </button>
              <button onClick={onContinue} className="flex-1 rounded-md px-3 py-1.5 text-[11px] font-semibold text-white" style={{ background: QA.purple }}>
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
